import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Medal, Target, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { localizedField } from '@/utils/localizedField';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import { ONBOARDING_CLOSED_EVENT } from '@/constants/events';
import { StoryCarousel } from '@/components/organisms/StoryCarousel';
import { useOnboardingPreferencesTrigger } from '@/hooks/useOnboardingPreferencesTrigger';
import { Carousel } from '@/components/organisms/Carousel';
import { StatCard } from '@/components/molecules/StatCard';
import { LevelTestBanner, useLevelTestBannerVisible } from '@/components/molecules/LevelTestBanner';
import { Spinner } from '@/components/atoms/Spinner';
import { ErrorState } from '@/components/molecules/ErrorState';
import { ROUTES, buildRoute } from '@/constants/routes';
import { getUserStreak, getCompanyRankings, getGlobalRankings } from '@/services/api/gamification.api';
import { usersAPI, type WeeklyGoalResponse as UserWeeklyGoal } from '@/services/api/users.api';
import { getWeeklyAssignments, type WeeklyAssignmentsResponse } from '@/services/api/weeklyAssignments.api';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { entranceAwareBlockTransition } from '@/constants/storyResourcesEntrance';
import type { StreakData } from '@/types/gamification.types';
import styles from './DashboardPage.module.scss';

const OFFSET_Y = 20;
const DURATION = 0.42;
const DELAYS = { welcome: 0, stats: 0.18, levelTest: 0.32, review: 0.34, stories: 0.5 } as const;

/**
 * Banner de "repaso" temporalmente oculto en el dashboard. Toda la
 * lógica detrás (fetch de items pendientes, animaciones, link a
 * `/student/review`) se mantiene intacta — solo el render del banner
 * está gateado por esta constante. Para volver a mostrarlo, poner a
 * true.
 */
const SHOW_REVIEW_BANNER = false;
/** Tras la entrada del banner de repaso: retos no comparten el mismo instante que el banner. */
const CHALLENGES_AFTER_REVIEW_GAP_S = 0.16;
const LEVEL_MAP: Record<number, string> = { 1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1', 6: 'C2' };
const HIDDEN_BLOCK = { opacity: 0, y: OFFSET_Y };
const VISIBLE_BLOCK = { opacity: 1, y: 0 };

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { labelFor } = useCategoryLabels();
  const { user } = useAuth();
  const navigate = useNavigate();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());

  const [weeklyAssignments, setWeeklyAssignments] = useState<WeeklyAssignmentsResponse | null>(null);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [assignmentsError, setAssignmentsError] = useState(false);
  // Se incrementa al cerrarse la modal de onboarding para recargar asignaciones
  // y meta (al completar la personalización se asignan historias).
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [isLoadingStreak, setIsLoadingStreak] = useState(true);
  const [weeklyGoal, setWeeklyGoal] = useState<UserWeeklyGoal | null>(null);
  const [isLoadingGoal, setIsLoadingGoal] = useState(true);
  const [topRanking, setTopRanking] = useState<
    Array<{ rank: number; name: string; xp: number; level: string; isCurrentUser?: boolean }>
  >([]);
  const [isLoadingRanking, setIsLoadingRanking] = useState(true);

  const [reviewDueCount, setReviewDueCount] = useState<number>(0);
  /** Evita animar retos antes que el banner: al refrescar, sin esto el count llega tarde y el carrusel entra primero. */
  const [reviewDueResolved, setReviewDueResolved] = useState(false);

  // Banner persistente de "personaliza tu experiencia": visible mientras el
  // alumno no haya guardado ninguna categoría. Independiente del modal y de
  // los skips — desaparece solo cuando se completa el onboarding.
  const { status: onboardingStatus, refetch: refetchOnboardingStatus } =
    useOnboardingPreferencesTrigger();
  const showPreferencesBanner =
    user?.role === 'student' && onboardingStatus?.hasSavedCategories === false;

  // Banner "haz el test de nivel" (persistente hasta completarlo). La lógica de
  // visibilidad y el propio banner viven en el componente reutilizable; aquí
  // solo necesitamos el flag para envolverlo en la animación de entrada.
  const showLevelTestBanner = useLevelTestBannerVisible();
  // Revela el contenido del banner (textos en cascada + CTA con rebote) una vez
  // el bloque ha terminado su animación de entrada, para que salga escalonado.
  const [levelBannerRevealed, setLevelBannerRevealed] = useState(false);

  useEffect(() => {
    if (!showLevelTestBanner || !pageReady) return;
    if (reducedMotion) {
      setLevelBannerRevealed(true);
      return;
    }
    const timer = setTimeout(
      () => setLevelBannerRevealed(true),
      (DELAYS.levelTest + DURATION) * 1000
    );
    return () => clearTimeout(timer);
  }, [showLevelTestBanner, pageReady, reducedMotion]);

  const fadeUp = (delay: number, opts?: { afterReviewDue?: boolean }) => {
    const ready = pageReady && (!opts?.afterReviewDue || reviewDueResolved);
    return {
      initial: HIDDEN_BLOCK,
      animate: ready ? VISIBLE_BLOCK : HIDDEN_BLOCK,
      transition: entranceAwareBlockTransition(ready, reducedMotion, DURATION, delay),
    };
  };

  useEffect(() => {
    const loadWeeklyAssignments = async () => {
      // Only load if user has a level (from level test)
      if (!user?.level || !user?.id) {
        setIsLoadingAssignments(false);
        return;
      }

      try {
        setIsLoadingAssignments(true);
        setAssignmentsError(false);
        const assignments = await getWeeklyAssignments();
        setWeeklyAssignments(assignments);
      } catch (error) {
        console.error('Failed to load weekly assignments:', error);
        setWeeklyAssignments(null);
        setAssignmentsError(true);
      } finally {
        setIsLoadingAssignments(false);
      }
    };

    void loadWeeklyAssignments();
  }, [user?.level, user?.id, refreshNonce]);

  useEffect(() => {
    const loadStreakData = async () => {
      if (!user?.id) {
        setIsLoadingStreak(false);
        return;
      }

      try {
        setIsLoadingStreak(true);
        const streak = await getUserStreak(user.id);
        setStreakData(streak);
      } catch (error) {
        console.error('Failed to load streak data:', error);
        setStreakData({
          currentStreak: 0,
          longestStreak: 0,
          totalActiveDays: 0,
          streakStartDate: null,
          lastActivityDate: null,
          hasActivityToday: false,
          isStreakAtRisk: false,
        });
      } finally {
        setIsLoadingStreak(false);
      }
    };

    void loadStreakData();
  }, [user?.id]);

  useEffect(() => {
    const loadWeeklyGoal = async () => {
      if (!user?.id) {
        setIsLoadingGoal(false);
        return;
      }

      try {
        setIsLoadingGoal(true);
        const goal = await usersAPI.getWeeklyGoal();
        setWeeklyGoal(goal);
      } catch (error) {
        console.error('Failed to load weekly goal:', error);
        setWeeklyGoal(null);
      } finally {
        setIsLoadingGoal(false);
      }
    };

    void loadWeeklyGoal();
  }, [user?.id, refreshNonce]);

  // Recarga al cerrarse la modal de onboarding (p. ej. completar personalización).
  // Además refrescamos el estado de onboarding (hasSavedCategories) para que el
  // banner "personaliza tu experiencia" desaparezca al instante, sin esperar a
  // un refresh o cambio de pantalla.
  useEffect(() => {
    const handler = () => {
      setRefreshNonce((n) => n + 1);
      void refetchOnboardingStatus();
    };
    window.addEventListener(ONBOARDING_CLOSED_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_CLOSED_EVENT, handler);
  }, [refetchOnboardingStatus]);

  useEffect(() => {
    if (!user?.id) {
      setReviewDueResolved(true);
      setReviewDueCount(0);
      return;
    }
    let cancelled = false;
    setReviewDueResolved(false);
    usersAPI
      .getReviewDue()
      .then((data) => {
        if (!cancelled) setReviewDueCount(data.totalItems);
      })
      .catch(() => {
        if (!cancelled) setReviewDueCount(0);
      })
      .finally(() => {
        if (!cancelled) setReviewDueResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const loadRankings = useCallback(
    async (showLoading = true) => {
      if (!user?.id) {
        setIsLoadingRanking(false);
        return;
      }

      try {
        if (showLoading) setIsLoadingRanking(true);
        const rankings = user.companyId
          ? await getCompanyRankings(user.companyId, 5)
          : await getGlobalRankings(5);

        const next = rankings.map((r) => ({
          rank: r.rank,
          name: r.name,
          xp: r.totalXP,
          level: LEVEL_MAP[r.level] ?? 'B1',
          isCurrentUser: r.userId === user.id,
        }));
        setTopRanking((prev) =>
          JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        );
      } catch (error) {
        console.error('Failed to load rankings:', error);
        setTopRanking([]);
      } finally {
        setIsLoadingRanking(false);
      }
    },
    [user?.id, user?.companyId]
  );

  useEffect(() => {
    void loadRankings();
  }, [loadRankings]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => { void loadRankings(false); }, 30000);
    return () => clearInterval(interval);
  }, [user?.id, loadRankings]);

  // Refetch when tab becomes visible (user returns to app)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) void loadRankings(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user?.id, loadRankings]);

  const currentUserRank = topRanking.find((r) => r.isCurrentUser)?.rank;

  const stats = useMemo(
    () => [
      {
        icon: Target,
        value: isLoadingGoal ? '...' : String(weeklyGoal?.storiesCompleted ?? 0),
        secondaryValue: weeklyGoal ? `/${weeklyGoal.targetStories}` : undefined,
        label: t('dashboard.weeklyGoal'),
      },
      {
        icon: Flame,
        value: isLoadingStreak ? '...' : String(streakData?.currentStreak ?? 0),
        label: t('dashboard.dayStreak'),
      },
      {
        icon: Medal,
        value: isLoadingRanking ? '...' : currentUserRank ? String(currentUserRank).padStart(2, '0') : '--',
        label: t('dashboard.rankingPosition'),
      },
    ],
    [
      isLoadingGoal,
      weeklyGoal,
      isLoadingStreak,
      streakData,
      isLoadingRanking,
      currentUserRank,
      t,
    ]
  );

  const stories = weeklyAssignments?.stories.map((story) => ({
    title: localizedField(story, 'title', i18n.language),
    description: localizedField(story, 'description', i18n.language),
    category: labelFor(story.category),
    estimatedTime: story.estimatedTime,
    xpReward: story.xpReward,
    completed: story.assignmentStatus === 'completed',
    backgroundImage: story.backgroundImage,
    variant: 'featured' as 'featured' | 'normal',
    onClick: () =>
      navigate(buildRoute(ROUTES.STUDENT.STORY, { id: story.id }), {
        state: { fromDashboard: true },
      }),
  })) || [];

  const challengesFadeDelay = useMemo(
    () =>
      reviewDueCount > 0
        ? DELAYS.review + DURATION + CHALLENGES_AFTER_REVIEW_GAP_S
        : DELAYS.stories,
    [reviewDueCount]
  );

  return (
    <div className={styles.dashboard}>
      <motion.section className={styles.dashboard__welcome} {...fadeUp(DELAYS.welcome)}>
        <h1 className={styles.dashboard__title}>
          {t('dashboard.welcome', { name: user?.name || t('common.studentFallbackName') })}
        </h1>
        <p className={styles.dashboard__subtitle}>{t('dashboard.subtitle')}</p>
      </motion.section>

      <motion.section className={styles.dashboard__stats} {...fadeUp(DELAYS.stats)}>
        <Carousel showHeader={false} showControls={false} showFades={false} statsCardWidth>
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </Carousel>
      </motion.section>

      {showLevelTestBanner && (
        <motion.section className={styles.dashboard__review} {...fadeUp(DELAYS.levelTest)}>
          <LevelTestBanner reveal={levelBannerRevealed} />
        </motion.section>
      )}

      {SHOW_REVIEW_BANNER && reviewDueCount > 0 && (
        <motion.section className={styles.dashboard__review} {...fadeUp(DELAYS.review, { afterReviewDue: true })}>
          <Link to={ROUTES.STUDENT.REVIEW} className={styles.dashboard__reviewBanner}>
            <div className={styles.dashboard__reviewContent}>
              <h2 className={styles.dashboard__reviewTitle}>{t('dashboard.reviewTitle')}</h2>
              <p className={styles.dashboard__reviewText}>{t('dashboard.reviewText')}</p>
            </div>
            <span className={styles.dashboard__reviewBtn} aria-hidden>
              <ArrowRight size={20} />
            </span>
          </Link>
        </motion.section>
      )}

      <motion.section className={styles.dashboard__challenges} {...fadeUp(challengesFadeDelay, { afterReviewDue: true })}>
        {isLoadingAssignments ? (
          <div className={styles.dashboard__loading}>
            <Spinner size="lg" aria-label={t('common.loading')} />
          </div>
        ) : !user?.level ? (
          <div className={styles.dashboard__empty}>
            <p>{t('dashboard.noLevelCta')}</p>
          </div>
        ) : assignmentsError ? (
          <ErrorState onRetry={() => setRefreshNonce((n) => n + 1)} />
        ) : !weeklyAssignments || stories.length === 0 ? (
          <div className={styles.dashboard__empty}>
            <p>{t('dashboard.noAssignments')}</p>
          </div>
        ) : (
          <StoryCarousel
            title={t('dashboard.continueTitle')}
            subtitle={t('dashboard.continueSubtitle')}
            stories={stories}
          />
        )}
      </motion.section>

      {showPreferencesBanner && (
        <motion.section
          className={styles.dashboard__review}
          {...fadeUp(DELAYS.stories + CHALLENGES_AFTER_REVIEW_GAP_S, { afterReviewDue: true })}
        >
          <Link
            to={ROUTES.STUDENT.ONBOARDING_PREFERENCES}
            className={styles.dashboard__reviewBanner}
          >
            <div className={styles.dashboard__reviewContent}>
              <h2 className={styles.dashboard__reviewTitle}>
                {t('onboardingPreferencesPrompt.title')}
              </h2>
              <p className={styles.dashboard__reviewText}>
                {t('onboardingPreferencesPrompt.description')}
              </p>
            </div>
            <span className={styles.dashboard__reviewBtn} aria-hidden>
              <ArrowRight size={20} />
            </span>
          </Link>
        </motion.section>
      )}

    </div>
  );
}

export default DashboardPage;
